import * as React from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { HistoricoGPS } from '../historicoGPS';
import { Num, SeletorStatus, statusTexto } from './index';
import ModalPassos, { PassoModal } from './ModalPassos';
import Modal, { BotaoSecundario, Campo, classesEntrada } from './Modal';
import { cn } from '../../lib/utils';
import { AutoCompleteMultiplo, buscarPaginado } from './AutoComplete';

// Mesmo endpoint e filtro de antes; agora so pagina a pagina, na lupa.
const buscarGrupos = buscarPaginado(
  (termo, pagina, linhas) =>
    `/entidades/paginado?page=${pagina}&rows=${linhas}&valor=${encodeURIComponent(termo)}&campo=nome`,
  (g) => ({ codigo: g.codigo, rotulo: g.nome })
);

// Mesmo teto da API (aplicativos.service.ts) - ver o seletor de grupos.
const MAX_GRUPOS_POR_VEZ = 10;

/* ============================================================================
   Cadastro de APLICATIVO em modal de cartoes.

   Substitui /dashboard/aplicativos/new/:packageName para criar e editar. A pagina
   antiga continua no ar.

   Conferido contra pages/dashboard/aplicativos/new/[packageName].tsx. Tres
   cartoes, que sao os tres blocos que a tela antiga tinha em ABAS:
     Dados principais · Grupos do aplicativo · Historico

   CARTOES E NAO ABAS de proposito. Aba esconde: com o bloco "Grupos" atras de um
   clique, ninguem lembra de conferir em quantos grupos o app esta antes de
   inativar - e inativar um app que esta em 40 grupos tira ele de 40 frotas. Em
   cartoes lado a lado a informacao esta a vista enquanto se mexe no status.

   QUATRO REGRAS DA TELA ANTIGA MANTIDAS:

   1. APLICATIVO APAGADO NAO PODE SER ALTERADO (`oldEntity.deletedAt`). O antigo
      avisava no submit; aqui o cartao ja abre em modo leitura, com o motivo.

   2. O PACOTE E IMUTAVEL na edicao (`disabled` quando ha packageName). E chave
      primaria e o Google identifica o app por ele.

   3. `dataCampo` (trilha de auditoria) so e montado na edicao.

   4. VINCULAR E DESVINCULAR GRUPO SAO REQUISICOES SEPARADAS
      (`adicionarAPlicativoNoGrupo` / `removerAPlicativoNoGrupo`), que gravam na
      hora e NAO fazem parte do salvamento do cadastro. Por isso o cartao de
      grupos e `opcional` e tem botoes proprios - juntar as duas coisas faria o
      "Salvar" disparar tres chamadas diferentes.

   O que mudou: as tres confirmacoes do SweetAlert2 viraram o Modal do padrao
   novo, e o `window.location.reload()` que vinha depois de cada uma virou
   recarga so da lista de grupos. Recarregar a pagina inteira fechava o modal e
   fazia o usuario procurar de novo onde estava.
   ============================================================================ */

type Grupo = { codigo: number; nome: string };
type GrupoVinculado = { GRUPO_ID: number; GRUPO_NOME: string; DISPOSITIVOS: number; POLITICA_NAME?: string };

export default function ModalAplicativo({
  aberto,
  packageName,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  /** `undefined` ou '0' = criar. Qualquer outro valor = editar aquele pacote. */
  packageName?: string;
  onFechar: () => void;
  onSalvo?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const editando = Boolean(packageName && packageName !== '0');

  const [nome, setNome] = React.useState('');
  const [pacote, setPacote] = React.useState('');
  const [status, setStatus] = React.useState('ATIVO');
  const [original, setOriginal] = React.useState<any>({});
  const [salvando, setSalvando] = React.useState(false);
  const [carregando, setCarregando] = React.useState(false);

  const [vinculados, setVinculados] = React.useState<GrupoVinculado[]>([]);
  const [selecionados, setSelecionados] = React.useState<Grupo[]>([]);
  const [operando, setOperando] = React.useState(false);

  // Confirmacoes em lote e por linha
  const [confirmar, setConfirmar] = React.useState<'adicionar' | 'remover' | null>(null);
  const [removerLinha, setRemoverLinha] = React.useState<GrupoVinculado | null>(null);

  const apagado = Boolean(original?.deletedAt);

  const [gruposFalhou, setGruposFalhou] = React.useState(false);

  const carregarGrupos = React.useCallback(async () => {
    if (!editando || !packageName) return;
    try {
      const r = await axios.get<GrupoVinculado[]>(`/aplicativos/mostrarGrupo/${encodeURIComponent(packageName)}`);
      setVinculados(Array.isArray(r.data) ? r.data : []);
      setGruposFalhou(false);
    } catch {
      // Falha NAO e "nenhum grupo": com isso a pessoa inativava o app sem ver o
      // alcance real (o aviso "Inativar tira este app de N grupos" sumia).
      setVinculados([]);
      setGruposFalhou(true);
    }
  }, [editando, packageName]);

  React.useEffect(() => {
    if (!aberto) return;

    setNome('');
    setPacote('');
    setStatus('ATIVO');
    setOriginal({});
    setVinculados([]);
    setSelecionados([]);

    if (!editando || !packageName) return;

    setCarregando(true);
    axios
      .get<any>(`/aplicativos/byId/${encodeURIComponent(packageName)}`)
      .then((r) => {
        const d = r.data || {};
        setOriginal(d);
        setNome(d.name ?? '');
        setPacote(d.packageName ?? '');
        setStatus(statusTexto(d.status));
      })
      .catch((e: any) =>
        enqueueSnackbar(e?.response?.data?.message || 'Não foi possível carregar o aplicativo.', {
          variant: 'error',
        })
      )
      .finally(() => setCarregando(false));

    carregarGrupos();
  }, [aberto, packageName, editando, enqueueSnackbar, carregarGrupos]);

  // --------------------------------------------------------------- validacao
  const nomeOk = nome.trim().length >= 1 && nome.trim().length <= 150;
  // Pacote so e digitado na CRIACAO; formato com ponto, como no cadastro rapido.
  const pacoteOk = editando ? true : pacote.trim().includes('.');

  // ---------------------------------------------------------------- acoes
  async function salvar() {
    if (salvando) return;
    if (apagado) {
      enqueueSnackbar('Aplicativo foi deletado, não pode alterar mais', { variant: 'warning' });
      return;
    }
    setSalvando(true);
    try {
      const diff: Array<{ campo: string; antigo: any; novo: any }> = [];
      if (editando) {
        if (nome !== original.name) diff.push({ campo: 'Nome', antigo: original.name, novo: nome });
        if (pacote !== original.packageName)
          diff.push({ campo: 'Pacote', antigo: original.packageName, novo: pacote });
        if (status !== original.status) diff.push({ campo: 'Status', antigo: original.status, novo: status });
      }

      const corpo = {
        name: nome.trim(),
        packageName: pacote.trim(),
        status,
        dataCampo: editando ? JSON.stringify(diff) : null,
      };

      if (editando) await axios.put(`/aplicativos/${encodeURIComponent(String(packageName))}`, corpo);
      else await axios.post('/aplicativos', corpo);

      enqueueSnackbar('Salvo com sucesso', { variant: 'success' });
      onSalvo?.();
      onFechar();
    } catch (err: any) {
      const e = err?.response?.data;
      const msg = e?.errors?.length > 0 ? e.errors[0]?.message : e?.message;
      enqueueSnackbar(msg || 'Ops ocorreu um erro no sistema.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setSalvando(false);
    }
  }

  async function aplicarEmLote(acao: 'adicionar' | 'remover') {
    if (!selecionados.length) {
      enqueueSnackbar('Nenhum grupo encontrado para adicionar', { variant: 'info' });
      return;
    }
    setOperando(true);
    try {
      const rota = acao === 'adicionar' ? 'adicionarAPlicativoNoGrupo' : 'removerAPlicativoNoGrupo';
      await axios.post(`/aplicativos/${rota}`, { lista: selecionados, packageName: original.packageName });
      enqueueSnackbar('Ação executada com sucesso', { variant: 'success' });
      setSelecionados([]);
      setConfirmar(null);
      // Recarrega SO a lista de grupos. O antigo dava window.location.reload(),
      // que fechava o modal e fazia o usuario procurar onde estava.
      await carregarGrupos();
      onSalvo?.();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Ops ocorreu um erro no sistema.', { variant: 'error' });
    } finally {
      setOperando(false);
    }
  }

  async function removerDoGrupo(row: GrupoVinculado) {
    setOperando(true);
    try {
      await axios.post('/aplicativos/removerAPlicativoNoGrupo', {
        lista: [{ codigo: row.GRUPO_ID, nome: row.GRUPO_NOME }],
        packageName: original.packageName,
      });
      enqueueSnackbar('Removido do grupo com sucesso', { variant: 'success' });
      setRemoverLinha(null);
      await carregarGrupos();
      onSalvo?.();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Ops ocorreu um erro no sistema.', { variant: 'error' });
    } finally {
      setOperando(false);
    }
  }

  const totalDispositivos = vinculados.reduce((soma, g) => soma + (Number(g.DISPOSITIVOS) || 0), 0);

  // -------------------------------------------------------------- cartoes
  const passos: PassoModal[] = [
    {
      id: 'dados',
      titulo: 'Dados principais',
      grupo: 'Cadastro',
      resumo: pacote.trim() || 'sem pacote',
      valido: nomeOk && pacoteOk && Boolean(status),
      pendencia: !nomeOk ? 'nome' : !pacoteOk ? 'pacote no formato com.exemplo.app' : 'status',
      conteudo: (
        <>
          {apagado && (
            <div className="rounded-lg border border-danger bg-danger-soft p-3 text-[12px] text-danger">
              Este aplicativo foi <strong>apagado</strong> e não pode mais ser alterado.
            </div>
          )}

          <Campo rotulo="Nome" obrigatorio>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={150}
              disabled={apagado}
              className={classesEntrada}
            />
          </Campo>

          <Campo
            rotulo="Pacote"
            obrigatorio={!editando}
            dica={
              editando
                ? 'Não pode ser alterado: é a chave do registro e é por ele que o Google identifica o app.'
                : 'Formato com.exemplo.app'
            }
          >
            <input
              value={pacote}
              onChange={(e) => setPacote(e.target.value)}
              // Imutavel na edicao, mesma regra do formulario antigo.
              disabled={editando || apagado}
              placeholder="com.exemplo.app"
              className={cn(classesEntrada, (editando || apagado) && 'opacity-60')}
            />
          </Campo>

          <Campo bloco rotulo="Status" obrigatorio>
            <SeletorStatus valor={status} onChange={setStatus} />

            {/* O alcance ao lado do status e o ponto do desenho em cartoes: quem
                inativa precisa ver de quantas frotas o app sai. */}
            {editando && status === 'INATIVO' && gruposFalhou && (
              <span className="mt-1.5 block text-[11px] text-danger">
                Não foi possível conferir em quantos grupos este app está — confira antes de inativar.
              </span>
            )}
            {editando && status === 'INATIVO' && vinculados.length > 0 && (
              <span className="mt-1.5 block text-[11px] text-warn">
                Inativar tira este app de <strong>{vinculados.length}</strong> grupo(s), alcançando{' '}
                <Num>{totalDispositivos.toLocaleString('pt-BR')}</Num> dispositivo(s).
              </span>
            )}
          </Campo>
        </>
      ),
    },
  ];

  passos.push({
    id: 'grupos',
    titulo: 'Grupos do aplicativo',
    grupo: 'Cadastro',
    resumo: vinculados.length ? `${vinculados.length} vinculado(s)` : 'nenhum',
    // Vincular/desvincular grava na hora, em requisicao propria - nao entra no
    // gate do Salvar.
    opcional: true,
    conteudo: (
      <>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-line bg-surface-2 p-2.5">
            <span className="block text-[10px] font-bold uppercase tracking-[0.07em] text-faint">Grupos</span>
            <Num className="block text-[17px] font-extrabold leading-tight">{vinculados.length}</Num>
          </div>
          <div className="rounded-lg border border-line bg-surface-2 p-2.5">
            <span className="block text-[10px] font-bold uppercase tracking-[0.07em] text-faint">
              Dispositivos
            </span>
            <Num className="block text-[17px] font-extrabold leading-tight">
              {totalDispositivos.toLocaleString('pt-BR')}
            </Num>
          </div>
        </div>

        <div className="max-h-[190px] overflow-y-auto rounded-lg border border-line">
          {vinculados.map((g) => (
            <div
              key={g.GRUPO_ID}
              className="flex items-center gap-2 border-b border-line px-2.5 py-1.5 last:border-b-0"
            >
              <span className="min-w-0 flex-1 truncate text-[12px] font-semibold">{g.GRUPO_NOME}</span>
              <Num className="flex-none rounded bg-soft px-1.5 text-[11px] text-muted-foreground">
                {Number(g.DISPOSITIVOS) || 0}
              </Num>
              <button
                type="button"
                onClick={() => setRemoverLinha(g)}
                className="flex-none text-[11px] font-semibold text-danger"
              >
                remover
              </button>
            </div>
          ))}
          {gruposFalhou && (
            <div className="px-3 py-4 text-center text-[12px] text-danger">
              Não foi possível carregar os grupos deste aplicativo.{' '}
              <button type="button" className="font-semibold underline" onClick={carregarGrupos}>
                tentar de novo
              </button>
            </div>
          )}
          {!gruposFalhou && !vinculados.length && (
            <div className="px-3 py-4 text-center text-[12px] text-faint">
              Este aplicativo não está em nenhum grupo — ele fica catalogado e não chega a dispositivo
              nenhum.
            </div>
          )}
        </div>

        <Campo bloco rotulo="Selecionar grupos" dica="Até 10 grupos por vez. Escolha e use um dos dois botões abaixo.">
          {/* As fichas do escolhido ganham peso aqui: os dois botoes abaixo
              GRAVAM na hora (adicionar/remover), entao ver exatamente o que esta
              selecionado antes de clicar deixou de depender de rolar a lista. */}
          {/* ATE 10 GRUPOS POR VEZ (29/09/2026): cada grupo regrava a politica no
              Google, em serie; a API recusa acima disso e o modal de selecao trava
              no teto. A lista vem paginada do servidor so quando se clica na lupa
              (21/10/2026) - antes eram 500 grupos baixados ao abrir o cadastro. */}
          <AutoCompleteMultiplo
            selecionados={selecionados.map((g) => ({ codigo: g.codigo, rotulo: g.nome }))}
            aoMudar={(l) => setSelecionados(l.map((o) => ({ codigo: Number(o.codigo), nome: o.rotulo })))}
            buscar={buscarGrupos}
            maximo={MAX_GRUPOS_POR_VEZ}
            titulo="Selecionar grupos"
            placeholder="Nome do grupo (Enter ou lupa)"
            vazio="Nenhum grupo encontrado."
          />
        </Campo>

        <div className="flex gap-2">
          <button
            type="button"
            disabled={operando || !selecionados.length}
            onClick={() => setConfirmar('adicionar')}
            className="h-9 flex-1 rounded-lg bg-primary text-[12px] font-bold text-primary-foreground disabled:opacity-40"
          >
            Adicionar ({selecionados.length})
          </button>
          <button
            type="button"
            disabled={operando || !selecionados.length}
            onClick={() => setConfirmar('remover')}
            className="h-9 flex-1 rounded-lg border border-danger text-[12px] font-bold text-danger disabled:opacity-40"
          >
            Remover ({selecionados.length})
          </button>
        </div>

        <p className="text-[11px] text-faint">
          Estas duas ações são independentes do botão Finalizar — elas gravam na hora.
        </p>
      </>
    ),
  });

  passos.push({
    id: 'historico',
    titulo: 'Histórico',
    grupo: 'Ferramentas',
    informativo: true,
    conteudo: original.packageName ? (
      <HistoricoGPS tipo={'APLICATIVO'} chave={original.packageName} />
    ) : (
      <div className="rounded-lg border border-line bg-surface-2 p-4 text-[12px] text-muted-foreground">
          O histórico começa a ser gravado quando este cadastro for salvo. Cada alteração
          posterior fica registrada aqui com quem fez, quando e o que mudou.
        </div>
    ),
  });

  return (
    <>
      <ModalPassos
        aberto={aberto}
        titulo={editando ? 'Editar aplicativo' : 'Novo aplicativo'}
        subtitulo={carregando ? 'Carregando...' : editando ? `${original.name ?? ''} · ${pacote}` : 'Novo aplicativo'}
        passos={passos}
        rotuloFinalizar={editando ? 'Salvar' : 'Criar aplicativo'}
        onFinalizar={salvar}
        onFechar={onFechar}
        salvando={salvando}
      />

      <Modal
        aberto={confirmar !== null}
        titulo={confirmar === 'adicionar' ? 'Adicionar aos grupos?' : 'Remover dos grupos?'}
        descricao={
          confirmar === 'adicionar'
            ? 'O aplicativo passa a ser distribuído em todos os grupos selecionados.'
            : 'O aplicativo deixa de ser distribuído nos grupos selecionados.'
        }
        icone="!"
        largura="max-w-[480px]"
        onFechar={() => setConfirmar(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setConfirmar(null)} disabled={operando}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => confirmar && aplicarEmLote(confirmar)}
              disabled={operando}
              className={cn(
                'h-9 rounded-lg px-4 text-[12.5px] font-bold text-white disabled:opacity-40',
                confirmar === 'adicionar' ? 'bg-primary' : 'bg-danger'
              )}
            >
              {operando ? 'Executando...' : confirmar === 'adicionar' ? 'Adicionar' : 'Remover'}
            </button>
          </>
        }
      >
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
          <span className="block font-semibold">{selecionados.length} grupo(s) selecionado(s)</span>
          <span className="mt-1 block text-muted-foreground">
            {selecionados.map((g) => g.nome).join(', ') || '—'}
          </span>
        </div>
      </Modal>

      <Modal
        aberto={Boolean(removerLinha)}
        titulo="Remover do grupo?"
        descricao="O aplicativo deixa de ser distribuído neste grupo."
        icone="!"
        largura="max-w-[440px]"
        onFechar={() => setRemoverLinha(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setRemoverLinha(null)} disabled={operando}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => removerLinha && removerDoGrupo(removerLinha)}
              disabled={operando}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {operando ? 'Removendo...' : 'Remover'}
            </button>
          </>
        }
      >
        {removerLinha && (
          <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
            <span className="block font-semibold">{removerLinha.GRUPO_NOME}</span>
            <span className="block text-muted-foreground">
              <Num>{Number(removerLinha.DISPOSITIVOS) || 0}</Num> dispositivo(s) deixam de receber o app.
            </span>
          </div>
        )}
      </Modal>
    </>
  );
}
